// SEE https://stripe.com/docs/payments/save-and-reuse?platform=web
import React, { useCallback, useContext, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import {
  PaymentElement,
  Elements as StripeProvider,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import config from 'config'

import Loading from 'tools/Loading'
import Modal from 'tools/Modal'
import notify from 'tools/Notify'
import {
  ActionTable as Atable,
  ActionTableBody as Atbody,
  ActionTableCell as Atd,
  ActionTableHeadCell as Ath,
  ActionTableHeading as Athead,
  ActionTableRow as Atr
} from 'tools/Table/Action'
import { CheckIcon, MaxViewPage } from 'tools/Uniform'
import { readableError } from 'utils/error'
import { noop } from 'utils/function'
import { stringify } from 'utils/string'

import { COMMIT_CUSTOMER, PAYMENT_METHODS, SETUP_CUSTOMER } from 'hub/Org/graphql'
import GlobalContext from 'reducer/global'
import { isThemeDark } from 'reducer/global/hooks'

import OrgContext from '../reducer'

loadStripe.setLoadParameters({ advancedFraudSignals: false })
const stripePromise = loadStripe(config?.stripeKey)

function formatDateString(date) {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  })
}

export default function Billing() {
  const [org] = useContext(OrgContext)

  return (
    <MaxViewPage>
      <h1 className="mb3">Billing</h1>

      <div className="pb4"></div>

      <PaymentMethods orgId={org.id} />
    </MaxViewPage>
  )

  // <h2 className="pv4">Credits</h2>
  // <h2 className="pv4">POs</h2>
  // <h2 className="pv4">Cost & Reports</h2>
}

function ListPaymentMethods({ cards, setActive, isLoading }) {
  return (
    <>
      <Atable className="list mb3 w-100">
        <Athead>
          <Ath>ACTIVE</Ath>
          <Ath>PROVIDER</Ath>
          <Ath>LAST 4 DIGITS</Ath>
          <Ath>CREATED</Ath>
          <Ath>EXPIRES</Ath>
        </Athead>
        <Atbody>
          {cards.map((method) => (
            <PaymentMethodRow
              key={method.id}
              method={method}
              onClick={() => setActive(method.id)}
            />
          ))}
        </Atbody>
      </Atable>
      {isLoading && <Loading />}
      {!isLoading && cards?.length === 0 && (
        <div className="pv2 pl2 i">No Cards on File</div>
      )}
    </>
  )
}

function PaymentMethodRow({
  method: { id, active, expiresOn, identifier, insertedAt, provider },
  onClick
}) {
  return (
    <Atr onClick={onClick} selected={active}>
      <Atd>
        <CheckIcon checked={active} />
      </Atd>
      <Atd>{provider}</Atd>
      <Atd>{identifier}</Atd>
      <Atd>{formatDateString(insertedAt)}</Atd>
      <Atd>{expiresOn}</Atd>
    </Atr>
  )
}

export function PaymentMethods({
  orgId,
  embed = false,
  onCardSelected = noop,
  onCardAdd = noop
}) {
  const [, dispatch] = useContext(GlobalContext)
  const [cards, setCards] = useState([])
  const [stripeOptions, setStripeOptions] = useState(undefined)
  const { loading: loadingCards, refetch: refetchCards } = useQuery(
    PAYMENT_METHODS,
    {
      fetchPolicy: 'cache-first',
      variables: {
        orgId
      },
      skip: !orgId,
      refetchQueries: ['bell'],
      onCompleted({ orgs }) {
        const cards = orgs?.results?.[0]?.paymentMethods
        if (orgs.success && Array.isArray(cards)) {
          setCards(cards)
        } else {
          notify.error(dispatch, readableError(orgs.reason))
        }
      }
    }
  )

  const [setupCustomer] = useMutation(SETUP_CUSTOMER)
  const [commitCustomer] = useMutation(COMMIT_CUSTOMER)
  const [showAddCardModal, setShowCardModal] = useState(false)
  const success = typeof stripeOptions === 'object'

  const onSetupCustomer = useCallback(() => {
    if (!orgId) return null
    return setupCustomer({ variables: { orgId } })
      .then(({ data: { orgChange } }) => {
        if (!orgChange.success) {
          return Promise.reject(orgChange.reason)
        }

        const { intent, processor } = orgChange.result

        if (processor !== 'stripe') {
          return Promise.reject(
            `expected processor to be "stripe", found: "${stringify(processor)}"`
          )
        }

        const options = {
          clientSecret: intent,
          appearance: {
            theme: isThemeDark() ? 'night' : undefined
          }
        }
        setStripeOptions(options)
        setShowCardModal(true)
      })
      .catch(notify.onCatch(dispatch))
  }, [orgId, setupCustomer, dispatch])

  const onSetActive = useCallback(
    (activeId) => {
      if (!orgId) return null
      return commitCustomer({
        variables: { orgId, activeId },
        refetchQueries: ['paymentMethods']
      })
        .then((res) => {
          onCardSelected()
          return res
        })
        .catch(notify.onCatch(dispatch))
    },
    [dispatch, commitCustomer, orgId, onCardSelected]
  )

  return (
    <div>
      {!embed && <h2 className="mv3">Payment Methods</h2>}
      {success && cards.length === 0 && (
        <i className="gray">
          Please provide a payment method for billing on your organization's
          account.
        </i>
      )}
      <ListPaymentMethods
        cards={cards}
        isLoading={loadingCards}
        setActive={onSetActive}
      />
      <button
        onClick={onSetupCustomer}
        disabled={!orgId}
        className="large w-100 clear tl"
      >
        <i className="fas fa-plus mr2" /> Add Payment
      </button>
      {success && stripeOptions && showAddCardModal && (
        <StripeProvider stripe={stripePromise} options={stripeOptions}>
          <AddCardModal
            onDiscard={() => setShowCardModal(false)}
            onCardAdd={(res) => {
              refetchCards()
                .then(({ data: { orgs } }) => {
                  if (!orgs.success) {
                    return Promise.reject(orgs.reason)
                  }
                  const paymentMethods = orgs.results?.[0]?.paymentMethods ?? []

                  const needsActiveCard =
                    paymentMethods.length > 0 &&
                    !paymentMethods.some(({ active }) => active)
                  if (needsActiveCard) {
                    onSetActive(paymentMethods[0].id)
                  }

                  onCardAdd(res)
                  setShowCardModal(false)
                  return null
                })
                .catch(notify.onCatch(dispatch))
            }}
          />
        </StripeProvider>
      )}
    </div>
  )
}

function AddCardModal({ onDiscard, onCardAdd }) {
  const [, dispatch] = useContext(GlobalContext)

  const stripe = useStripe()
  const elements = useElements()

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()

      if (!stripe || !elements) {
        return null
      }

      stripe
        .confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href
          },
          redirect: 'if_required'
        })
        .then((res) => {
          if (res?.error) {
            return Promise.reject(res.error.message)
          }
          return onCardAdd(res)
        })
        .catch(notify.onCatch(dispatch))
    },
    [stripe, elements, dispatch, onCardAdd]
  )

  return (
    <Modal
      viewState={[true, onDiscard]}
      width="fw-50"
      noScroll={true}
      className="base-frame pa4"
    >
      <div className="f3 b mb3">Add New Card</div>
      <form onSubmit={handleSubmit}>
        <PaymentElement />
        <div className="mt3 flex justify-between">
          <button className="large plain" onClick={onDiscard}>
            Close
          </button>
          <button className="large" disabled={!stripe}>
            Submit
          </button>
        </div>
      </form>
    </Modal>
  )
}

// <ul>ul
//   <li> Bills</li>
//   <li> Payments</li>
//   <li> Credits</li>
//   <li> POs</li>
//   <li> Cost & Reports</li>
// </ul>

// <h2 className={`${style.span2} mt4 mb3`}>Payment Method</h2>
// <div className="b tr mr2 items-center">Credit Card:</div>
// <Input value={'0000-xxxxx-xxxxxxx-xxxxx'} onChange={() => {}} />
